import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, AlertCircle, Phone, Mail, Calendar, Shield, User } from "lucide-react";

export default function HealthPassPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${apiBase}/api/health-pass/public/${token}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(async (r) => {
        const detail = await r?.json?.().catch(() => null);
        setError(detail?.detail || "Passeport invalide ou expiré.");
      });
  }, [token, apiBase]);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Shield size={48} className="mx-auto mb-4 text-impact-red"/>
          <h1 className="font-display font-black text-3xl mb-3">PASSEPORT INVALIDE</h1>
          <p className="text-white/70 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Chargement du passeport…</div>;
  }

  const isComp = data.variant === "competition";
  const exp = data.expires_at ? new Date(data.expires_at * 1000).toLocaleDateString("fr-CA") : null;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header bandeau or */}
      <header className="border-b border-impact-gold/40 px-6 py-5 flex items-center justify-between bg-gradient-to-r from-black via-zinc-900 to-black">
        <div>
          <div className="ticker text-impact-gold mb-1">— {isComp ? "PASSEPORT COMPÉTITION ATHLÈTE" : "CARTE SANTÉ DOJO"}</div>
          <div className="text-xs text-white/50">{data.issued_by_dojo}</div>
        </div>
        <Shield size={32} className={isComp ? "text-impact-red" : "text-impact-gold"}/>
      </header>

      {/* Identité */}
      <section className="px-6 py-8 border-b border-white/10" data-testid="pass-identity">
        <h1 className="font-display font-black text-4xl mb-2">{data.full_name}</h1>
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="bg-impact-gold/15 text-impact-gold px-3 py-1 ticker">{data.discipline?.replace(/_/g, " ").toUpperCase()}</span>
          {data.niveau_ceinture && <span className="bg-white/10 px-3 py-1 ticker">CEINTURE : {data.niveau_ceinture.toUpperCase()}</span>}
          {data.groupe_age && <span className="bg-white/10 px-3 py-1 ticker">{data.groupe_age.replace(/_/g, " ").toUpperCase()}</span>}
          {data.date_of_birth && (
            <span className="bg-white/10 px-3 py-1 ticker flex items-center gap-1"><Calendar size={12}/> {data.date_of_birth}</span>
          )}
        </div>
      </section>

      {/* Contact urgence — toujours rendu */}
      <section className="px-6 py-6 border-b border-white/10 bg-impact-red/10" data-testid="pass-emergency">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle size={18} className="text-impact-red"/>
          <h2 className="ticker text-impact-red">CONTACT URGENCE</h2>
        </div>
        {data.contact_urgence && (data.contact_urgence.parent_name || data.contact_urgence.parent_email || data.contact_urgence.phone) ? (
          <div className="space-y-2 text-sm">
            {data.contact_urgence.parent_name && <div className="flex items-center gap-2"><User size={14}/> {data.contact_urgence.parent_name}</div>}
            {data.contact_urgence.phone && <div className="flex items-center gap-2"><Phone size={14}/> <a href={`tel:${data.contact_urgence.phone}`} className="hover:text-impact-gold">{data.contact_urgence.phone}</a></div>}
            {data.contact_urgence.parent_email && <div className="flex items-center gap-2"><Mail size={14}/> <a href={`mailto:${data.contact_urgence.parent_email}`} className="hover:text-impact-gold">{data.contact_urgence.parent_email}</a></div>}
          </div>
        ) : (
          <p className="text-xs text-white/50 italic">Aucun contact d'urgence enregistré.</p>
        )}
      </section>

      {/* Santé — variant regular */}
      {!isComp && (
        <section className="px-6 py-6 border-b border-white/10" data-testid="pass-health">
          <h2 className="ticker text-impact-gold mb-3">ANTÉCÉDENTS MÉDICAUX</h2>
          {data.sante?.antecedents_medicaux?.length > 0 ? (
            <div className="space-y-2">
              {data.sante.antecedents_medicaux.map((e) => (
                <div key={e.id} className="border-l-2 border-impact-gold/40 pl-3 text-sm">
                  <div className="font-bold">{e.title}</div>
                  <div className="text-xs text-white/50">{e.entry_date} · {e.entry_type}</div>
                  {e.content && <div className="text-white/80 mt-1 whitespace-pre-wrap text-xs">{e.content}</div>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-white/50 italic">Aucun antécédent médical enregistré.</p>
          )}
        </section>
      )}

      {/* Compétition — variant competition */}
      {isComp && (
        <>
          <section className="px-6 py-6 border-b border-white/10" data-testid="pass-promotions">
            <h2 className="ticker text-impact-gold mb-3 flex items-center gap-2"><Award size={16}/> PROMOTIONS CEINTURES</h2>
            {data.competition?.promotions?.length > 0 ? (
              <div className="space-y-2">
                {data.competition.promotions.map((e) => (
                  <div key={e.id} className="border-l-2 border-impact-gold pl-3 text-sm">
                    <div className="font-mono">{e.ceinture_precedente || "—"} → <strong className="text-impact-gold">{e.ceinture_nouvelle}</strong></div>
                    <div className="text-xs text-white/50">{e.entry_date} {e.author_name ? `· ${e.author_name}` : ""}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-white/50 italic">Aucune promotion enregistrée.</p>
            )}
          </section>
          <section className="px-6 py-6 border-b border-white/10" data-testid="pass-injuries">
            <h2 className="ticker text-impact-red mb-3 flex items-center gap-2"><AlertCircle size={16}/> HISTORIQUE BLESSURES</h2>
            {data.competition?.blessures_historiques?.length > 0 ? (
              <div className="space-y-2">
                {data.competition.blessures_historiques.map((e) => (
                  <div key={e.id} className="border-l-2 border-impact-red/60 pl-3 text-sm">
                    <div className="flex items-center gap-2">
                      <strong>{e.title}</strong>
                      {e.severity && <span className="ticker text-[10px] bg-impact-red/30 px-2 py-0.5">{e.severity.toUpperCase()}</span>}
                    </div>
                    <div className="text-xs text-white/50">{e.entry_date}</div>
                    {e.content && <div className="text-white/80 mt-1 text-xs whitespace-pre-wrap">{e.content}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-white/50 italic">Aucune blessure enregistrée.</p>
            )}
          </section>
        </>
      )}

      <footer className="px-6 py-6 text-xs text-white/40 text-center border-t border-white/10">
        Passeport sécurisé · Mon Assistant Pro · {exp && `Valide jusqu'au ${exp}`}
      </footer>
    </div>
  );
}
